/* eslint-disable @typescript-eslint/no-explicit-any */
import TreeNode from './TreeNode';
import TreeDataController from '../../../../common/generic/tree/TreeDataController';
import {
  TTreeProps,
  TDataNode,
  TId,
} from '../../../../common/generic/tree/types';
import {
  TApiMethods,
  TApiState,
  TOnStateUpdate,
  TNodeSearchCallback,
} from './types';
import { deleteApiaTreeApi, setApiaTreeApi } from './useTreeApi';

export default class ApiaTreeApi implements TApiMethods {
  #state: TApiState = {};

  constructor(private id: string, private handler: TreeDataController<any>) {
    setApiaTreeApi(this.id, this);
  }

  destroy() {
    deleteApiaTreeApi(this.id);
    this.#onStateUpdateCallbacks = [];
  }

  updateHandler(handler: TreeDataController<any>) {
    this.handler = handler;
  }

  #setState(updateState: Partial<TApiState>) {
    this.#state = { ...this.#state, ...updateState };
    this.handler.setConfiguration(updateState);
    this.#fireStateUpdate();
  }

  #onStateUpdateCallbacks: TOnStateUpdate[] = [];

  #fireStateUpdate() {
    this.#onStateUpdateCallbacks.forEach((current) => current(this.#state));
  }

  /** Selection */

  collapseNode(id: TId) {
    this.handler.toggleNodeExpandedState(id, false);
  }

  deselectNode(id: TId) {
    this.handler.toggleNodeSelectedState(id, false);
  }

  expandNode(id: TId) {
    this.handler.toggleNodeExpandedState(id, true);
  }

  getExpandedNodes() {
    return this.handler.state.expandedNodes;
  }

  getSelectedNodes() {
    return this.handler.state.selectedNodes;
  }

  selectNode(id: TId) {
    this.handler.toggleNodeSelectedState(id, true);
  }

  setFocusedNode(id: TId) {
    this.handler.setFocusedNode(id);
  }

  setExpandedNodes(nodes: TId[], force = false) {
    this.handler.setExpandedNodes(nodes, force);
  }

  setSelectedNodes(nodes: TId[], force = false) {
    this.handler.setSelectedNodes(nodes, force);
  }

  /** Events */

  onExpand(callback: TTreeProps['onExpand']) {
    this.#setState({ onExpand: callback });
  }

  onNodeClick(callback: TTreeProps['onNodeClick']) {
    this.#setState({ onNodeClick: callback });
  }

  onSelect(callback: TTreeProps['onSelect']) {
    this.#setState({ onSelect: callback });
  }

  /** Nodes list */

  addNode(node: TDataNode) {
    this.handler.append(node);
  }

  findNode(searchCallback: TNodeSearchCallback) {
    const node = this.handler.getNodesAsArray().find(searchCallback);

    if (node) return new TreeNode(node.id, this.handler);

    return null;
  }

  getNodeById(id: TId) {
    const node = this.handler.propsStore.getFieldProps(id);

    if (node) return new TreeNode(node.id, this.handler);

    return null;
  }

  removeNode(id: TId) {
    this.handler.remove(id);
  }

  /** Behavior methods */

  onStateUpdate(cb: TOnStateUpdate) {
    this.#onStateUpdateCallbacks.push(cb);

    return () => {
      this.#onStateUpdateCallbacks = this.#onStateUpdateCallbacks.filter(
        (current) => current !== cb,
      );
    };
  }

  setHideEmptyFolders(hide: boolean) {
    this.#setState({ hideEmptyFolders: hide });
  }

  setIsExpansionEnabled(isEnabled: boolean) {
    this.#setState({ disableExpand: !isEnabled });
  }

  setIsMultiple(isMultiple: boolean) {
    this.#setState({ isMultiple });
  }

  setIsSelectionEnabled(isEnabled: boolean) {
    this.#setState({ disableSelection: !isEnabled });
  }

  setSelectionMode(mode: 'onFocus' | 'explicit' | undefined) {
    this.#setState({ selectionMode: mode });
  }

  setToggleNodesOnClickLabel(toggle: boolean) {
    this.#setState({ toggleNodesOnLabelClick: toggle });
  }

  setVariant(variant: string) {
    this.#setState({ variant });
  }
}
